import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import { allComics } from '@/mocks/comics';

const statusColorMap: Record<string, string> = {
  '连载中': 'bg-accent-100 text-accent-900',
  '已完结': 'bg-secondary-100 text-secondary-900',
};

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const comicId = parseInt(id || '0', 10);
  const comic = allComics.find((c) => c.id === comicId);
  const relatedComics = comic
    ? allComics.filter((c) => comic.relatedIds.includes(c.id))
    : [];

  const jsonLd = comic
    ? {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'http://emenculture.com/' },
              { '@type': 'ListItem', position: 2, name: '全部分类', item: 'http://emenculture.com/category' },
              { '@type': 'ListItem', position: 3, name: `${comic.title}`, item: `http://emenculture.com/article/${comic.id}` },
            ],
          },
          {
            '@type': 'Article',
            headline: `${comic.title} - 好看的韩国漫画`,
            description: comic.description,
            author: { '@type': 'Person', name: comic.author },
            datePublished: comic.chapters_list[comic.chapters_list.length - 1]?.date,
            dateModified: comic.chapters_list[0]?.date,
            image: comic.image,
            url: `http://emenculture.com/article/${comic.id}`,
            publisher: { '@id': 'http://emenculture.com/#organization' },
          },
          {
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: `${comic.title}是免费观看的吗？`,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: `是的，${comic.title}在韩漫网完全免费观看，提供高清画质，无需付费即可享受完整阅读体验。`,
                },
              },
              {
                '@type': 'Question',
                name: `${comic.title}的更新频率如何？`,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: `${comic.title}每周定期更新，${comic.status}，最新章节为${comic.chapters_list[0]?.title || comic.chapter}。`,
                },
              },
            ],
          },
        ],
      }
    : {};

  useEffect(() => {
    if (comic) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
      return () => {
        document.head.removeChild(script);
      };
    }
  }, [id]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center px-6">
            <h1 className="font-heading text-4xl font-black text-foreground-900">漫画未找到</h1>
            <p className="mt-3 text-foreground-500">该漫画不存在或已被下架</p>
            <Link to="/category" className="mt-6 inline-flex items-center gap-2 text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
              <i className="ri-arrow-left-line"></i> 浏览其他漫画
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main>
        <section className="pt-24 pb-10 md:pt-32 md:pb-14">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to="/category" className="hover:text-primary-500 transition-colors cursor-pointer">全部分类</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-900 font-medium">{comic.title}</span>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
              <div className="lg:col-span-4">
                <div className="sticky top-24">
                  <div className="w-full max-w-[320px] mx-auto aspect-[3/4] rounded-3xl overflow-hidden bg-background-200">
                    <img
                      src={comic.image}
                      alt={`好看的韩国漫画 - ${comic.title}`}
                      title={`好看的韩国漫画 - ${comic.title}`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>

              <div className="lg:col-span-8">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColorMap[comic.status] || 'bg-background-200 text-foreground-600'}`}>
                    {comic.status}
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-primary-100 text-primary-700">
                    {comic.tag}
                  </span>
                </div>
                <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black text-foreground-900">
                  {comic.title}
                </h1>
                <p className="mt-3 text-sm text-foreground-500">
                  作者：<span className="text-foreground-700 font-medium">{comic.author}</span>
                </p>
                <p className="mt-6 text-sm md:text-base text-foreground-700 leading-relaxed max-w-2xl">
                  {comic.description}
                </p>

                <div className="flex flex-wrap items-center gap-3 mt-8">
                  <Link
                    to={`/article/${comic.id}`}
                    className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-8 py-3 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-play-circle-line text-lg"></i>
                    开始阅读
                  </Link>
                  <button className="inline-flex items-center gap-2 border border-background-300 text-foreground-700 px-8 py-3 rounded-full text-sm font-semibold hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-bookmark-line text-lg"></i>
                    加入收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">章节目录</h2>
              <div className="flex items-center gap-2 text-sm text-foreground-500">
                <span>共 {comic.chapters} 话</span>
                <span className="text-foreground-300">|</span>
                <button className="text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">倒序</button>
              </div>
            </div>

            <div className="bg-background-50 rounded-2xl overflow-hidden border border-background-200/70">
              <div className="divide-y divide-background-200/70 max-h-[600px] overflow-y-auto">
                {comic.chapters_list.map((ch) => (
                  <Link
                    key={ch.num}
                    to={`/article/${comic.id}`}
                    className="flex items-center justify-between px-5 py-4 hover:bg-background-100 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-10 h-10 rounded-full bg-background-200 flex items-center justify-center text-sm font-semibold text-foreground-600 group-hover:bg-primary-100 group-hover:text-primary-700 transition-colors">
                        {ch.num}
                      </span>
                      <div>
                        <p className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors">
                          第{ch.num}话{' '}
                          <span className="text-foreground-400 font-normal">{ch.title}</span>
                        </p>
                      </div>
                    </div>
                    <span className="text-xs text-foreground-400 whitespace-nowrap">{ch.date}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="py-16 md:py-24">
            <div className="max-w-[1440px] mx-auto px-6 md:px-10">
              <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-10">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-900">相关推荐</h2>
                  <p className="mt-2 text-sm text-foreground-500">你可能也会喜欢</p>
                </div>
                <Link
                  to="/category"
                  className="text-sm text-foreground-600 hover:text-primary-500 transition-colors flex items-center gap-1 whitespace-nowrap cursor-pointer"
                >
                  浏览更多
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} {...c} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}